import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store';
import { isBuyerCapable } from '~/utils';
import { useGetOrganizationsPaginated } from '~/api/organizations';
import { useDebouncedSearchInput } from '~/hooks/useDebouncedSearchInput';
import OrganizationsTableCard from '~/components/common/OrganizationsTableCard';
import { linkedOrganizationPath } from '~/utils/linkedOrganizationPath';
import { SellerListsSection } from './sections/SellerListsSection';
import { TenantAdminExportCsvButton } from './sections/TenantAdminExportCsvButton';
import { useLinkedOrganizationUsersCsvExport } from './sections/useLinkedOrganizationUsersCsvExport';

export default function LinkedOrganizations() {
  const navigate = useNavigate();
  const { t } = useTranslation('organizations');
  const isBuyer = useSelector((state: RootState) =>
    isBuyerCapable(state.auth.user?.role)
  );
  const { exportAllLinkedOrganizationUsers, isExportingAll } =
    useLinkedOrganizationUsersCsvExport();
  const { searchInputValue, activeSearch, handleSearchChange } =
    useDebouncedSearchInput();
  const {
    data: linkedOrganizations,
    sentinelRef,
    isLoading,
    pagination,
  } = useGetOrganizationsPaginated(20, activeSearch);

  return (
    <div className="w-full flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold text-neut-prim">
          {t('organizations.linked.title')}
        </h1>
        <p className="text-sm text-neut-sec mt-1">
          {t('organizations.linked.subtitle')}
        </p>
      </div>

      {isBuyer && <SellerListsSection />}

      <OrganizationsTableCard
        organizations={linkedOrganizations}
        tableName="linked-organizations"
        isLoading={isLoading}
        totalElements={pagination?.totalElements ?? 0}
        sentinelRef={sentinelRef}
        searchInputValue={searchInputValue}
        onSearchChange={handleSearchChange}
        onOpenOrganization={(organization) =>
          navigate(linkedOrganizationPath(organization.id))
        }
        headerActions={
          <TenantAdminExportCsvButton
            disabled={isExportingAll}
            onClick={() => void exportAllLinkedOrganizationUsers()}
          />
        }
      />
    </div>
  );
}
